import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type { LearningStatus, LearningType } from '@/types/learning';

interface UseLearnedContextProps {
  orgId: number | undefined;
  userId: number | undefined;
  /** Poll for updates until learning is complete (default: false) */
  pollUntilComplete?: boolean;
  /** Polling interval in ms (default: 5000) */
  pollInterval?: number;
}

export function useLearnedContext({
  orgId,
  userId,
  pollUntilComplete = false,
  pollInterval = 5000,
}: UseLearnedContextProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [learningStatus, setLearningStatus] = useState<LearningStatus | null>(
    null
  );
  const [triggeringLearning, setTriggeringLearning] = useState(false);
  const [expandedProfile, setExpandedProfile] = useState<string | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRequestInFlightRef = useRef(false);

  const loadLearningData = useCallback(async () => {
    if (!orgId || !userId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load learning status
      const statusResponse = await fetchWithDefaults(
        `/api/orgs/${orgId}/learning/users/${userId}/status/`
      );

      if (statusResponse.ok) {
        const statusData: LearningStatus = await statusResponse.json();
        setLearningStatus(statusData);
      } else {
        logger.error(
          `Failed to load learning status (HTTP ${statusResponse.status})`,
          undefined,
          { component: 'useLearnedContext' }
        );
        setError("We couldn't load your learning status. Try again.");
      }
    } catch (err) {
      logger.error(
        'Error loading learning data',
        err instanceof Error ? err : undefined,
        { component: 'useLearnedContext' }
      );
      setError("We couldn't load your learning data. Try again.");
    } finally {
      setLoading(false);
    }
  }, [orgId, userId]);

  const handleTriggerLearning = useCallback(
    async (learningTypes: LearningType[]) => {
      if (!orgId || !userId) {
        return;
      }

      try {
        setTriggeringLearning(true);
        setError(null);

        const response = await fetchWithDefaults(
          `/api/orgs/${orgId}/learning/users/${userId}/trigger/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ learning_types: learningTypes }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || errorData.detail || 'Failed to trigger learning'
          );
        }

        // Reload data after triggering
        setTimeout(() => loadLearningData(), 2000);
      } catch (err) {
        logger.error(
          'Error triggering learning',
          err instanceof Error ? err : undefined,
          { component: 'useLearnedContext' }
        );
        setError("We couldn't trigger learning. Try again.");
      } finally {
        setTriggeringLearning(false);
      }
    },
    [orgId, userId, loadLearningData]
  );

  // Check if learning is complete (user_context profile exists)
  const isLearningComplete =
    learningStatus?.learned_profiles?.some(
      profile => profile.profile_type === 'user_context'
    ) ?? false;

  useEffect(() => {
    if (orgId && userId) {
      loadLearningData();
    }
  }, [orgId, userId, loadLearningData]);

  // Polling effect - polls until learning is complete
  useEffect(() => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Only poll if enabled, we have valid IDs, and learning is not complete
    if (pollUntilComplete && orgId && userId && !isLearningComplete) {
      pollIntervalRef.current = setInterval(async () => {
        if (isPollingRequestInFlightRef.current) {
          return;
        }
        isPollingRequestInFlightRef.current = true;
        try {
          await loadLearningData();
        } finally {
          isPollingRequestInFlightRef.current = false;
        }
      }, pollInterval);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [
    pollUntilComplete,
    orgId,
    userId,
    isLearningComplete,
    pollInterval,
    loadLearningData,
  ]);

  return {
    loading,
    error,
    learningStatus,
    triggeringLearning,
    expandedProfile,
    setExpandedProfile,
    loadLearningData,
    handleTriggerLearning,
    isLearningComplete,
  };
}
